import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandLogo from "@/components/BrandLogo";
import { MailCheck } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

export default function Register() {
  const { applyNeutral, exitNeutral } = useBranding();
  const [form, setForm] = useState({ nom_agence: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { applyNeutral(); return () => exitNeutral(); }, [applyNeutral, exitNeutral]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await api.post("/auth/register", form); setDone(true); }
    catch (err) { setError(apiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dd-night p-6">
      <div className="w-full max-w-md bg-white rounded-lg p-8" data-testid="register-page">
        <div className="mb-6"><BrandLogo className="h-12" /></div>
        {done ? (
          <div data-testid="register-done">
            <MailCheck className="text-dd-blue mb-3" size={32} />
            <h2 className="text-2xl font-bold text-dd-blue">Vérifiez votre boîte e-mail</h2>
            <p className="mt-3 text-sm text-dd-night">Un lien de confirmation a été envoyé à <strong>{form.email}</strong>. Cliquez dessus pour activer votre gérance <strong>{form.nom_agence}</strong>, puis connectez-vous.</p>
            <p className="mt-3 text-xs text-dd-warm">Rien reçu ? Vérifiez vos indésirables ou <button className="underline text-dd-blue" onClick={() => api.post("/auth/resend-verification", { email: form.email })} data-testid="resend-verify">renvoyer l'e-mail</button>.</p>
            <Link to="/login" className="mt-6 block text-sm text-dd-blue hover:underline">Retour à la connexion</Link>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-dd-blue">Créer un compte de gérance</h2>
            <p className="text-sm text-dd-warm mt-1">Votre gérance dispose de ses propres constats, réglages, logo et utilisateurs. Vous en serez l'administrateur.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label className="text-dd-night">Nom de la gérance</Label><Input value={form.nom_agence} onChange={set("nom_agence")} required className="h-11 mt-1" placeholder="Ex. Régie du Lac SA" data-testid="register-agence" /></div>
              <div><Label className="text-dd-night">Votre nom</Label><Input value={form.name} onChange={set("name")} required className="h-11 mt-1" data-testid="register-name" /></div>
              <div><Label className="text-dd-night">E-mail professionnel</Label><Input type="email" value={form.email} onChange={set("email")} required className="h-11 mt-1" data-testid="register-email" /></div>
              <div><Label className="text-dd-night">Mot de passe (8 caractères min.)</Label><Input type="password" value={form.password} onChange={set("password")} required minLength={8} className="h-11 mt-1" data-testid="register-password" /></div>
              {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-11 bg-dd-blue hover:bg-dd-hover text-white" data-testid="register-submit">{loading ? "Création…" : "Créer ma gérance"}</Button>
            </form>
            <div className="mt-5 flex justify-between text-sm">
              <Link to="/login" className="text-dd-blue hover:underline">Déjà un compte ? Se connecter</Link>
              <Link to="/forgot-password" className="text-dd-blue hover:underline">Mot de passe oublié ?</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
